import {
  Grid,
  Paper,
  Typography,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import { Box } from "@mui/system";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { sellerProductsForProfile, SORT_BY } from "../../redux/actions/productActions";
import { CenteredLoading } from "../common/CenteredLoading";
import {
  fetchWishlistProducts,
  PAGE_SIZE as WISHLIST_PAGE_SIZE,
} from "../../redux/actions/wishlistAction";

interface IDashboardProps {
  profileId: string | number | null;
  setSelectedTab: Dispatch<SetStateAction<number>>;
  setDefaultFilterInMyListing: Dispatch<SetStateAction<SORT_BY>>;
}

export const Dashboard: React.FC<IDashboardProps> = ({
  profileId,
  setSelectedTab,
  setDefaultFilterInMyListing,
}) => {
  const dispatch = useDispatch();

  const { loading, productsData } = useSelector(
    (state: RootState) => state.products
  );
  const { totalRecords: activeListingsCount } = productsData;

  const { wishlistData } = useSelector((state: RootState) => state.wishlists);
  const { totalRecords: wishlistTotalRecords } = wishlistData;

  // Until this component's own scoped fetch has completed at least once,
  // `productsData`/`wishlistData` may still reflect a stale, unrelated fetch
  // from another page (e.g. the marketplace feed) sitting in the shared
  // redux store. Keep showing the loading state rather than briefly
  // rendering a stale count.
  const sawOwnLoadingRef = useRef(false);

  useEffect(() => {
    sawOwnLoadingRef.current = false;
    dispatch(
      sellerProductsForProfile(Number(profileId), "", SORT_BY.ACTIVE)
    );
    dispatch(fetchWishlistProducts(Number(profileId), "", WISHLIST_PAGE_SIZE));
  }, [profileId, dispatch]);

  useEffect(() => {
    if (loading) {
      sawOwnLoadingRef.current = true;
    }
  }, [loading]);

  const showLoading = loading || !sawOwnLoadingRef.current;

  const [expanded, setExpanded] = useState<string | false>(false);
  const handleAccordionChange =
    (panel: string) => (_: any, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  const statCards = [
    { title: "Total Sales", value: "$0.00" },
    { title: "Items Sold", value: "0", tabToOpen: 2 },
    {
      title: "Active Listings",
      value: activeListingsCount || 0,
      tabToOpen: 3,
    },
    {
      title: "Wishlist Items",
      value: wishlistTotalRecords || 0,
      tabToOpen: 4,
    },
  ];

  return showLoading ? (
    <CenteredLoading />
  ) : (
    <Box>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={3}
      >
        <Typography
          variant="h6"
          fontWeight="bold"
          sx={{ fontSize: "1.4rem", color: "#1d1d1d" }}
        >
          Account Overview
        </Typography>
        <Tooltip title="Settings">
          <IconButton
            onClick={() => setSelectedTab(5)}
            sx={{
              background: "#f2f2f2",
              "&:hover": { background: "#e7e7e7" },
            }}
          >
            <SettingsIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2}>
        {statCards.map((item, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Paper
              onClick={() => {
                if (item.tabToOpen) {
                  setSelectedTab(item.tabToOpen);
                  setDefaultFilterInMyListing(SORT_BY.ACTIVE);
                }
              }}
              sx={{
                p: 2.5,
                borderRadius: "14px",
                background: "linear-gradient(135deg,#fff 0%,#f9f9f9 100%)",
                outline: "1px solid #eaeaea",
                boxShadow: "0px 2px 10px rgba(0,0,0,0.05)",
                cursor: item.tabToOpen ? "pointer" : "default",
                transition: "0.25s",
                "&:hover": {
                  transform: item.tabToOpen ? "translateY(-4px)" : "none",
                  boxShadow: item.tabToOpen
                    ? "0px 6px 14px rgba(0,0,0,0.07)"
                    : "0px 2px 10px rgba(0,0,0,0.05)",
                },
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "0.95rem",
                  color: "#555",
                }}
              >
                {item.title}
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  mt: 1,
                  color: "rgb(231,135,88)",
                  letterSpacing: 0.5,
                }}
              >
                {item.value}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* FAQ Section */}
      <Box mt={5}>
        <Typography
          variant="h6"
          fontWeight="bold"
          mb={2}
          sx={{ fontSize: "1.35rem", color: "#1d1d1d" }}
        >
          Frequently Asked Questions
        </Typography>

        {/* ✅ ORIGINAL FAQ CONTENT UNTOUCHED ✅ */}
        <Accordion
          expanded={expanded === "panel1"}
          onChange={handleAccordionChange("panel1")}
          sx={{
            mb: 2,
            borderRadius: "10px",
            outline: "1px solid #e6e6e6",
            "&:before": { display: "none" },
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight="bold">Account & Settings</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2">
              <strong>How do I update my account details?</strong>
              <br />
              Go to your Dashboard Settings to update your email, password, or
              payout details.
            </Typography>
            <Typography variant="body2" mt={1.5}>
              <strong>How do I change my payout details?</strong>
              <br />
              Navigate to Dashboard Payout Settings and update your payment
              information (powered by Stripe).
            </Typography>
          </AccordionDetails>
        </Accordion>

        <Accordion
          expanded={expanded === "panel2"}
          onChange={handleAccordionChange("panel2")}
          sx={{
            mb: 2,
            borderRadius: "10px",
            outline: "1px solid #e6e6e6",
            "&:before": { display: "none" },
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight="bold">Selling & Orders</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2">
              <strong>How do I list a new product?</strong>
              <br />
              From your dashboard, click "Add Product", upload images, set a
              price, and enter shipping details.
            </Typography>
            <Typography variant="body2" mt={1.5}>
              <strong>Can I upload multiple products at once?</strong>
              <br />
              Yes! Contact us for bulk uploads at hello@fiddlepiddle.com.
            </Typography>
            <Typography variant="body2" mt={1.5}>
              <strong>How do I know when I make a sale?</strong>
              <br />
              You’ll receive an email notification, and the sale details will
              appear in your Dashboard Sales section.
            </Typography>
            <Typography variant="body2" mt={1.5}>
              <strong>Where can I find the buyer’s shipping details?</strong>
              <br />
              In your Dashboard Sales, click on the order to view shipping info.
            </Typography>
            <Typography variant="body2" mt={1.5}>
              <strong>How long do I have to ship an order?</strong>
              <br />
              You should ship within 24-72 hours. If there’s a delay, notify the
              buyer.
            </Typography>
            <Typography variant="body2" mt={1.5}>
              <strong>How do I update the tracking number?</strong>
              <br />
              Go to Dashboard Sales, select the order, and enter the tracking
              number.
            </Typography>
          </AccordionDetails>
        </Accordion>

        <Accordion
          expanded={expanded === "panel3"}
          onChange={handleAccordionChange("panel3")}
          sx={{
            mb: 2,
            borderRadius: "10px",
            outline: "1px solid #e6e6e6",
            "&:before": { display: "none" },
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight="bold">Payments & Fees</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2">
              <strong>How do I get paid?</strong>
              <br />
              Payments are processed through Stripe and deposited directly into
              your bank account.
            </Typography>
            <Typography variant="body2" mt={1.5}>
              <strong>How often do payouts occur?</strong>
              <br />
              Payouts are typically made every 2 days, but the first payout may
              take up to 14 days.
            </Typography>
            <Typography variant="body2" mt={1.5}>
              <strong>What are the selling fees?</strong>
              <br />
              FiddlePiddle takes a 7.5% service fee per sale, no matter the item
              price.
            </Typography>
          </AccordionDetails>
        </Accordion>

        <Accordion
          expanded={expanded === "panel4"}
          onChange={handleAccordionChange("panel4")}
          sx={{
            mb: 2,
            borderRadius: "10px",
            outline: "1px solid #e6e6e6",
            "&:before": { display: "none" },
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight="bold">Disputes & Protection</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2">
              <strong>What happens if a buyer disputes an order?</strong>
              <br />
              We mediate disputes if the item is not as described. We also
              monitor frequent refund requests to prevent fraud.
            </Typography>
            <Typography variant="body2" mt={1.5}>
              <strong>What if an order gets lost in transit?</strong>
              <br />
              If tracking shows the item was shipped but not delivered, the
              seller can file a claim with the shipping carrier. If tracking
              shows delivery, the buyer is responsible.
            </Typography>
          </AccordionDetails>
        </Accordion>

        <Typography variant="body2" mt={2}>
          <strong>Need More Help?</strong>
          <br />
          If you have any issues or questions, email us at{" "}
          <a href="mailto:hello@fiddlepiddle.com" target="_blank">
            hello@fiddlepiddle.com
          </a>
          .
        </Typography>
      </Box>
    </Box>
  );
};
